import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilAltIcon, TrashIcon } from '@heroicons/react/solid';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import AddExperienceModal from './components/AddExperienceModal';
import EditExperienceModal from './components/EditExperienceModal';
import { toastService } from '../../../services/toast';
import { api } from '../../../services/api';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { usePermissions } from '../../../contexts/PermissionsContext';

const ExperiencesPage = () => {
  const [experiences, setExperiences] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const { hasPermission } = usePermissions();

  // دالة جلب البيانات من الباك إند
  const fetchExperiences = async () => {
    try {
      const response = await api.experiences.getAll();
      setExperiences(response.data);
    } catch (error) {
      console.error('Error:', error);
      toastService.error('حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  // جلب البيانات عند تحميل الكومبوننت
  useEffect(() => {
    fetchExperiences();
  }, []);

  const handleAdd = async (formData) => {
    try {
      await api.experiences.create(formData);
      toastService.success('تمت إضافة التجربة بنجاح');
      fetchExperiences();
      setShowAddModal(false);
    } catch (error) {
      toastService.error('حدث خطأ أثناء إضافة التجربة');
      console.error('Error adding experience:', error);
    }
  };

  const handleEdit = async (formData) => {
    try {
      await api.experiences.update(selectedExperience.id, formData);
      toastService.success('تم تعديل التجربة بنجاح');
      fetchExperiences();
      setShowEditModal(false);
      setSelectedExperience(null);
    } catch (error) {
      toastService.error('حدث خطأ أثناء تحديث التجربة');
      console.error('Error updating experience:', error);
    }
  };

  // دالة حذف التجربة
  const handleDelete = async () => {
    if (!selectedExperience) return;
    
    try {
      await api.experiences.delete(selectedExperience.id);
      toastService.success('تم الحذف بنجاح');
      setShowDeleteModal(false);
      fetchExperiences(); // إعادة تحميل البيانات بعد الحذف
    } catch (error) {
      console.error('Error:', error);
      toastService.error('حدث خطأ أثناء الحذف');
    }
  };

  // دالة تحويل الأرقام العربية إلى إنجليزية
  const toEnglishNumbers = (num) => {
    return num.toString().replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));
  };

  // دالة تنسيق التاريخ
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${toEnglishNumbers(date.getDate())}/${toEnglishNumbers(date.getMonth() + 1)}/${toEnglishNumbers(date.getFullYear())}`;
  };

  // تصفية التجارب بناءً على البحث
  const filteredExperiences = experiences.filter(exp =>
    searchQuery === '' ||
    exp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    exp.url?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6">
      {/* بطاقة العنوان والبحث والأيقونات */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">تجارب المشتركين</h2>
          <div className="flex gap-4">
            {hasPermission('create_review') && (
              <div className="relative group overflow-visible">
                <PlusIcon 
                  className="h-6 w-6 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 cursor-pointer transition-colors duration-200"
                  onClick={() => setShowAddModal(true)}
                  aria-hidden="true"
                />
                <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                  إضافة تجربة جديدة
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="p-6">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">البحث عن تجربة</label>
            <div className="relative">
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-blue-500 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="البحث عن تجربة..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-base pr-10 w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* بطاقة جدول تجارب المشتركين */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        {loading ? (
          <div className="text-center py-8">جاري التحميل...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    الاسم
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    الرابط
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32">
                    تاريخ الإضافة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredExperiences.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      لا توجد تجارب متاحة
                    </td>
                  </tr>
                ) : (
                  filteredExperiences.map((exp) => (
                    <tr key={exp.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {exp.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {exp.url}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(exp.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-3 space-x-reverse">
                          {hasPermission('edit_review') && (
                            <button
                              onClick={() => {
                                setSelectedExperience(exp);
                                setShowEditModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                              title="تعديل"
                            >
                              <PencilAltIcon className="h-5 w-5" />
                            </button>
                          )}
                          {hasPermission('delete_review') && (
                            <button
                              onClick={() => {
                                setSelectedExperience(exp);
                                setShowDeleteModal(true);
                              }}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              title="حذف"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* مودال إضافة تجربة جديدة */}
      <AddExperienceModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAdd}
      />

      {/* مودال تعديل تجربة */}
      {selectedExperience && (
        <EditExperienceModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedExperience(null);
          }}
          onSubmit={handleEdit}
          initialData={selectedExperience}
        />
      )}

      {/* مودال تأكيد الحذف */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="حذف تجربة"
        description="هل أنت متأكد أنك تريد حذف هذه التجربة؟"
      />

    </div>
  );
};

export default React.memo(ExperiencesPage);
